import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import './App.css';

const API_BASE = window.location.origin;

function Leaderboard() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Search wird jetzt in Header.js verwaltet
  
  // Filter State
  const [bondingRateMin, setBondingRateMin] = useState(5);
  const [bondingRateMax, setBondingRateMax] = useState(100);
  const [avgMarketcapMin, setAvgMarketcapMin] = useState('');
  const [avgMarketcapMax, setAvgMarketcapMax] = useState('');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    loadLeaderboard();
    
    // Polling alle 30 Sekunden
    const pollInterval = setInterval(() => {
      loadLeaderboard(false);
    }, 30000);

    return () => {
      clearInterval(pollInterval);
    };
  }, []);

  const loadLeaderboard = async (showLoading = true, retryCount = 0) => {
    try {
      setError(null);
      if (showLoading) {
        setLoading(true);
      }
      // Lade JSON-Datei direkt über Apache (OHNE Express = SUPER SCHNELL!)
      const response = await axios.get(`/api/leaderboard/realtime?t=${Date.now()}`, {
        timeout: 10000,
        validateStatus: function (status) {
          return status >= 200 && status < 300;
        }
      });
      
      const leaderboardData = response.data.data || [];
      setLeaderboard(leaderboardData);
      setLoading(false);
    } catch (err) {
      console.error('[API] Error loading leaderboard:', err);
      
      // Retry-Logik mit exponential backoff
      if (retryCount < 3 && (err.code === 'ERR_NETWORK' || err.response?.status >= 500)) {
        console.log(`[API] Retry ${retryCount + 1}/3 in ${1000 * (retryCount + 1)}ms...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return loadLeaderboard(showLoading, retryCount + 1);
      }
      
      setError(err.message || 'Failed to load leaderboard');
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 'N/A';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toFixed(2);
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " years ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " months ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hours ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " min ago";
    return Math.floor(seconds) + " seconds ago";
  };

  // handleSearch wird jetzt in Header.js verwaltet

  // Filter and Pagination Logic
  const getFilteredAndPaginatedData = () => {
    let filtered = [...leaderboard];
    
    // Filter by Bonding Rate
    filtered = filtered.filter(dev => {
      const bondingRate = parseFloat(dev.bonding_rate_percent || 0);
      return bondingRate >= bondingRateMin && bondingRate <= bondingRateMax;
    });
    
    // Filter by Average Marketcap
    if (avgMarketcapMin) {
      const minMC = parseFloat(avgMarketcapMin);
      if (!isNaN(minMC)) {
        filtered = filtered.filter(dev => {
          const avgMC = parseFloat(dev.average_marketcap_usd || 0);
          return avgMC >= minMC;
        });
      }
    }
    
    if (avgMarketcapMax) {
      const maxMC = parseFloat(avgMarketcapMax);
      if (!isNaN(maxMC)) {
        filtered = filtered.filter(dev => {
          const avgMC = parseFloat(dev.average_marketcap_usd || 0);
          return avgMC <= maxMC;
        });
      }
    }
    
    // Pagination
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);
    
    return {
      filtered,
      paginated,
      totalPages,
      totalCount: filtered.length
    };
  };

  const { filtered, paginated, totalPages, totalCount } = getFilteredAndPaginatedData();
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [bondingRateMin, bondingRateMax, avgMarketcapMin, avgMarketcapMax]);

  if (loading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="error-container">
          <p>❌ Error: {error}</p>
          <button onClick={loadLeaderboard} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Header />

      <main className="app-main">
        <div className="leaderboard-container">
          <h2 className="leaderboard-title">Dev Leaderboard</h2>
          <p className="leaderboard-subtitle">Top Devs sorted by Bonding Rate ({totalCount} devs)</p>
          
          {/* Filters */}
          <div className="leaderboard-filters">
            <div className="filter-group">
              <label htmlFor="bonding-rate-min">Bonding Rate: {bondingRateMin}% - {bondingRateMax}%</label>
              <div className="range-inputs">
                <input
                  type="range"
                  id="bonding-rate-min"
                  min="5"
                  max="100"
                  value={bondingRateMin}
                  onChange={(e) => setBondingRateMin(parseInt(e.target.value))}
                  className="slider"
                />
                <input
                  type="range"
                  id="bonding-rate-max"
                  min="5"
                  max="100"
                  value={bondingRateMax}
                  onChange={(e) => setBondingRateMax(parseInt(e.target.value))}
                  className="slider"
                />
              </div>
            </div>
            
            <div className="filter-group">
              <label htmlFor="avg-marketcap-min">Average Marketcap (USD):</label>
              <div className="range-inputs">
                <input
                  type="number"
                  id="avg-marketcap-min"
                  placeholder="Min"
                  value={avgMarketcapMin}
                  onChange={(e) => setAvgMarketcapMin(e.target.value)}
                  className="marketcap-input"
                />
                <input
                  type="number"
                  id="avg-marketcap-max"
                  placeholder="Max"
                  value={avgMarketcapMax}
                  onChange={(e) => setAvgMarketcapMax(e.target.value)}
                  className="marketcap-input"
                />
              </div>
            </div>
          </div>
          
          {/* Desktop Table */}
          <div className="table-wrapper desktop-layout">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Dev Wallet</th>
                  <th>Total Launches</th>
                  <th>Bonded Count</th>
                  <th>Bonding Rate</th>
                  <th>Highest Marketcap</th>
                  <th>Average Marketcap</th>
                  <th>Last Launch</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                      No devs found
                    </td>
                  </tr>
                ) : (
                  paginated.map((dev, index) => {
                    const globalRank = (currentPage - 1) * itemsPerPage + index + 1;
                    return (
                      <tr key={dev.creator}>
                        <td className="rank-cell">#{globalRank}</td>
                        <td>
                          <span 
                            className="dev-wallet-link"
                            onClick={() => navigate(`/dev/${encodeURIComponent(dev.creator)}`)}
                          >
                            {dev.creator}
                          </span>
                        </td>
                        <td className="number-cell">{dev.totallaunches}</td>
                        <td className="number-cell">{dev.bondedcount}</td>
                        <td className="bonding-rate-cell">{dev.bonding_rate_percent}%</td>
                        <td className="mc-cell">${formatNumber(dev.highest_marketcap_usd)}</td>
                        <td className="mc-cell">${formatNumber(dev.average_marketcap_usd)}</td>
                        <td className="time-cell">{formatTimeAgo(dev.last_launch_timestamp || dev.last_launch_createdat)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Mobile Cards */}
          <div className="mobile-layout leaderboard-mobile">
            {paginated.length === 0 ? (
              <div className="mobile-empty-state">
                <p>No devs found</p>
              </div>
            ) : (
              paginated.map((dev, index) => {
                const globalRank = (currentPage - 1) * itemsPerPage + index + 1;
                return (
                  <div key={dev.creator} className="leaderboard-mobile-card">
                    <div className="mobile-rank-header">
                      <span className="mobile-rank">#{globalRank}</span>
                      <span className="mobile-bonding-rate">{dev.bonding_rate_percent}%</span>
                    </div>
                    
                    <div 
                      className="mobile-dev-wallet clickable"
                      onClick={() => navigate(`/dev/${encodeURIComponent(dev.creator)}`)}
                    >
                      {dev.creator}
                    </div>
                    
                    <div className="mobile-stats-grid">
                      <div className="mobile-stat-item">
                        <div className="mobile-stat-label">Total Launches</div>
                        <div className="mobile-stat-value">{dev.totallaunches}</div>
                      </div>
                      <div className="mobile-stat-item">
                        <div className="mobile-stat-label">Bonded</div>
                        <div className="mobile-stat-value">{dev.bondedcount}</div>
                      </div>
                    </div>
                    
                    <div className="mobile-mc-grid">
                      <div className="mobile-mc-item">
                        <div className="mobile-mc-label">Highest MC</div>
                        <div className="mobile-mc-value">${formatNumber(dev.highest_marketcap_usd)}</div>
                      </div>
                      <div className="mobile-mc-item">
                        <div className="mobile-mc-label">Avg MC</div>
                        <div className="mobile-mc-value">${formatNumber(dev.average_marketcap_usd)}</div>
                      </div>
                    </div>
                    
                    <div className="mobile-last-launch">
                      <span className="mobile-time-label">Last Launch:</span>
                      <span className="mobile-time-value">{formatTimeAgo(dev.last_launch_timestamp || dev.last_launch_createdat)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="page-btn"
              >
                ← Prev
              </button>
              <span className="page-info">Page {currentPage} of {totalPages}</span>
              <button 
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </main>

      <footer className="app-footer">
        <p>DevRadar.fun - Real-time Dev Launches Tracker</p>
        <p>Only Devs with Bonding Rate ≥ 5% and more than 1 Coin</p>
      </footer>
    </div>
  );
}

export default Leaderboard;

